import {
  Box,
  Button,
  Card,
  CardHeader,
  Chip,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import { useCallback, useRef, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { TpmsDevice, TpmsSensor } from "src/models";
import useModelData from "src/hooks/use-model-data";
import { DisplayPageData } from "src/components/display-page-data";
import Iconify from "src/components/iconify/iconify";
import SimpleDialog from "src/components/simple-dialog";
import TruxFormField from "src/components/inputs/trux-form-field";
import TruxSimpleTextField from "src/components/inputs/trux-simple-text-field";

import PageHeader from "src/components/page-header";
import { useParams } from "src/routes/hooks";
import { fDateTime, fToNow } from "src/utils/format-time";
import {
  barToPsi,
  celsiusToFahrenheit,
  decodeTpmsStatus,
  formatReportedPosition,
  hasTpmsReported,
  isSensorStale,
  sensorSeverity,
  slotsForVehicleType,
} from "src/utils/tpms-utils";
import { useEventListener } from "src/hooks/use-event-listener";
import { severityColor, VehicleDiagram } from "./_vehicle_diagram";

type PageModel = {
  tpmsDevice?: TpmsDevice,
}

// HTML5 drag and drop does not fire on touch-only devices
const isTouchDevice = typeof window !== 'undefined'
  && window.matchMedia('(hover: none) and (pointer: coarse)').matches;

type SensorStatusChipsProps = {
  sensor: TpmsSensor,
};

function SensorStatusChips({ sensor }: SensorStatusChipsProps) {
  const flags = decodeTpmsStatus(sensor.status);
  const chips: { label: string, color: "error" | "warning" | "default" }[] = [];

  if (flags.lowPressure) chips.push({ label: "Low Pressure", color: "error" });
  if (flags.highPressure) chips.push({ label: "High Pressure", color: "error" });
  if (flags.highTemperature) chips.push({ label: "High Temperature", color: "error" });
  if (flags.leak === 'emergency') chips.push({ label: "Emergency Leak", color: "error" });
  if (flags.leak === 'slow') chips.push({ label: "Slow Leak", color: "warning" });
  if (flags.leak === 'inflating') chips.push({ label: "Inflating", color: "warning" });
  if (flags.lowBattery) chips.push({ label: "Low Battery", color: "warning" });
  if (flags.noRecentData || isSensorStale(sensor)) chips.push({ label: "Stale", color: "default" });

  if (chips.length === 0) {
    return <Chip size="small" color="success" label="Normal" />;
  }

  return (
    <Stack direction="row" flexWrap="wrap" gap={0.5}>
      {chips.map(chip => (
        <Chip key={chip.label} size="small" color={chip.color} label={chip.label} />
      ))}
    </Stack>
  );
}

type SensorInfoRowProps = {
  label: string,
  children: React.ReactNode,
};

function SensorInfoRow({ label, children }: SensorInfoRowProps) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="body2" component="div">{children}</Typography>
    </Stack>
  );
}

export default function Page() {
  const settings = useSettingsContext();
  const { id } = useParams();
  const dataLoader = useCallback(() => ({
    tpmsDevice: axios.get(formatEndpoint(endpoints.tpmsDevices.findById, { id })).then(response => response.data),
  }), [id]);
  const {
    data,
    updateData,
    error,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  const [selectedSensorId, setSelectedSensorId] = useState<string | null>(null);
  const [draggingSensorId, setDraggingSensorId] = useState<string | null>(null);
  const [isOpenRename, setIsOpenRename] = useState(false);
  const [isOpenRemoveSensor, setIsOpenRemoveSensor] = useState(false);
  const [newDeviceName, setNewDeviceName] = useState("");
  const [renaming, setRenaming] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const unassignedSensorsRef = useRef<HTMLDivElement>(null);
  const sensorDetailsRef = useRef<HTMLDivElement>(null);

  const tpmsDevice = data?.tpmsDevice;
  const slots = tpmsDevice ? slotsForVehicleType(tpmsDevice.vehicleType) : [];
  const slotCount = slots.length;

  const unassignedSensors = tpmsDevice?.sensors?.filter(
    sensor => sensor.wheelSlot === null || sensor.wheelSlot < 0 || sensor.wheelSlot >= slotCount
  ) || [];
  const selectedSensor = tpmsDevice?.sensors?.find(sensor => sensor.sensorId === selectedSensorId);

  const handleClickAway = (event: MouseEvent) => {
    if (selectedSensorId === null || isOpenRemoveSensor) return;
    const target = event.target as Node;
    if (
      containerRef.current?.contains(target) ||
      unassignedSensorsRef.current?.contains(target) ||
      sensorDetailsRef.current?.contains(target)
    ) return;
    setSelectedSensorId(null);
  };
  useEventListener('click', handleClickAway);

  const saveArrangement = useCallback((next: TpmsDevice, removedSensorIds: string[] = []) => {
    updateData("tpmsDevice")(next);
    axios.post(endpoints.tpmsDevices.updateArrangement, {
      deviceId: next.id,
      sensors: next.sensors.map(sensor => ({ sensorId: sensor.sensorId, wheelSlot: sensor.wheelSlot })),
      removedSensorIds,
    }).then(response => {
      updateData("tpmsDevice")(response.data);
    }).catch(() => {
      enqueueSnackbar("Could not save the tire arrangement", { variant: "error" });
      setShouldRefresh(true);
    });
  }, [updateData, setShouldRefresh]);

  const onWheelTap = useCallback((slotIndex: number) => {
    if (!tpmsDevice) {
      return;
    }
    const sensorInSlot = tpmsDevice.sensors.find(sensor => sensor.wheelSlot === slotIndex);
    if (!sensorInSlot) {
      return;
    }
    setSelectedSensorId(sensorInSlot.sensorId === selectedSensorId ? null : sensorInSlot.sensorId);
  }, [tpmsDevice, selectedSensorId]);

  const onUnassignedSensorTap = useCallback((sensorId: string) => {
    setSelectedSensorId(selectedSensorId === sensorId ? null : sensorId);
  }, [selectedSensorId]);

  const onDragStart = useCallback((sensorId: string) => {
    setDraggingSensorId(sensorId);
  }, []);

  const onDragEnd = useCallback(() => {
    setDraggingSensorId(null);
  }, []);

  const onDropOnSlot = useCallback((slotIndex: number) => {
    // state setters are async, so draggingSensorId below still holds its pre-drop value this cycle
    setDraggingSensorId(null);
    if (!tpmsDevice || draggingSensorId === null) {
      return;
    }
    const dragged = tpmsDevice.sensors.find(sensor => sensor.sensorId === draggingSensorId);
    if (!dragged || dragged.wheelSlot === slotIndex) {
      return;
    }
    const draggedSlot = dragged.wheelSlot !== null && dragged.wheelSlot >= 0 && dragged.wheelSlot < slotCount
      ? dragged.wheelSlot
      : null;
    const sensorInSlot = tpmsDevice.sensors.find(sensor => sensor.wheelSlot === slotIndex);

    saveArrangement({
      ...tpmsDevice,
      sensors: tpmsDevice.sensors.map(sensor => {
        if (sensor.sensorId === dragged.sensorId) {
          return { ...sensor, wheelSlot: slotIndex };
        }
        if (sensorInSlot && sensor.sensorId === sensorInSlot.sensorId) {
          return { ...sensor, wheelSlot: draggedSlot };
        }
        return sensor;
      }),
    });
  }, [tpmsDevice, draggingSensorId, slotCount, saveArrangement]);

  const unassignSelected = useCallback(() => {
    if (!tpmsDevice || !selectedSensor || selectedSensor.wheelSlot === null) {
      return;
    }
    saveArrangement({
      ...tpmsDevice,
      sensors: tpmsDevice.sensors.map(sensor =>
        sensor.sensorId === selectedSensor.sensorId ? { ...sensor, wheelSlot: null } : sensor
      ),
    });
    setSelectedSensorId(null);
  }, [tpmsDevice, selectedSensor, saveArrangement]);

  const removeSelected = useCallback(() => {
    if (!tpmsDevice || !selectedSensor) {
      return;
    }
    saveArrangement({
      ...tpmsDevice,
      sensors: tpmsDevice.sensors.filter(sensor => sensor.sensorId !== selectedSensor.sensorId),
    }, [selectedSensor.sensorId]);
    setSelectedSensorId(null);
    setIsOpenRemoveSensor(false);
  }, [tpmsDevice, selectedSensor, saveArrangement]);

  const renameDevice = useCallback(() => {
    if (!tpmsDevice || newDeviceName.trim() === "") {
      return;
    }
    setRenaming(true);
    axios.post(endpoints.devices.updateDeviceType, {
      deviceId: tpmsDevice.id,
      deviceType: newDeviceName.trim(),
    }).then(() => {
      enqueueSnackbar("Successfully renamed the device");
      updateData("tpmsDevice")({ ...tpmsDevice, deviceName: newDeviceName.trim() });
      setIsOpenRename(false);
    }).catch(() => {
      enqueueSnackbar("Could not rename the device", { variant: "error" });
    }).finally(() => {
      setRenaming(false);
    });
  }, [tpmsDevice, newDeviceName, updateData]);

  const selectedSlotLabel = selectedSensor && selectedSensor.wheelSlot !== null
    ? slots.find(slot => slot.index === selectedSensor.wheelSlot)?.label
    : undefined;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        {tpmsDevice && <>
          <PageHeader
            title={tpmsDevice.deviceName}
            tag={
              <Chip
                size="small"
                variant="outlined"
                label={tpmsDevice.vehicleType === 'trailer' ? 'Trailer' : 'Truck'}
              />
            }
            subtitle={
              <Typography variant="body2" color="text.secondary">
                {hasTpmsReported(tpmsDevice.lastReportedAt)
                  ? `Last report ${fDateTime(tpmsDevice.lastReportedAt)} (${fToNow(tpmsDevice.lastReportedAt)})`
                  : "No reports yet"}
              </Typography>
            }
          >
            <Button
              variant="outlined"
              startIcon={<Iconify icon="solar:pen-bold" />}
              onClick={() => {
                setNewDeviceName(tpmsDevice.deviceName);
                setIsOpenRename(true);
              }}
            >
              Rename
            </Button>
          </PageHeader>

          <Grid container spacing={3}>
            <Grid item xs={12} md={7}>
              <Card sx={{ p: 3 }} ref={containerRef}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  {isTouchDevice
                    ? "To rearrange the tires, please log in on a desktop."
                    : "Drag a tire onto another tire or an empty wheel to swap or move it."}
                </Typography>
                <VehicleDiagram
                  vehicleType={tpmsDevice.vehicleType}
                  sensors={tpmsDevice.sensors || []}
                  selectedSensorId={selectedSensorId}
                  draggingSensorId={draggingSensorId}
                  onWheelTap={onWheelTap}
                  onDragStart={onDragStart}
                  onDragEnd={onDragEnd}
                  onDrop={onDropOnSlot}
                />
              </Card>
            </Grid>

            <Grid item xs={12} md={5}>
              <Stack spacing={3}>
                <Card ref={unassignedSensorsRef}>
                  <CardHeader title="Unassigned Sensors" />
                  <Box sx={{ p: 3, pt: 2 }}>
                    {unassignedSensors.length === 0 &&
                      <Typography variant="body2" color="text.secondary">
                        {tpmsDevice.sensors?.length
                          ? "All sensors are assigned to a wheel."
                          : "No sensors have reported yet."}
                      </Typography>
                    }
                    <Stack direction="row" flexWrap="wrap" gap={1}>
                      {unassignedSensors.map(sensor => (
                        <Chip
                          key={sensor.sensorId}
                          label={`${sensor.sensorId} · ${barToPsi(sensor.pressureBar).toFixed(1)} psi`}
                          onClick={() => onUnassignedSensorTap(sensor.sensorId)}
                          variant={selectedSensorId === sensor.sensorId ? "filled" : "outlined"}
                          draggable
                          onDragStart={(event: React.DragEvent) => {
                            event.dataTransfer.effectAllowed = "move";
                            // defer so the drag image is captured before styles change
                            requestAnimationFrame(() => onDragStart(sensor.sensorId));
                          }}
                          onDragEnd={onDragEnd}
                          sx={{
                            cursor: "grab",
                            // own stacking context so the drag ghost is just the chip, not the card
                            position: "relative",
                            zIndex: 1,
                            bgcolor: selectedSensorId === sensor.sensorId
                              ? severityColor(sensorSeverity(sensor))
                              : undefined,
                            color: selectedSensorId === sensor.sensorId ? "common.white" : undefined,
                            ...(draggingSensorId !== null && draggingSensorId !== sensor.sensorId && {
                              opacity: 0.45,
                            }),
                          }}
                        />
                      ))}
                    </Stack>
                  </Box>
                </Card>

                <Card ref={sensorDetailsRef}>
                  <CardHeader title="Sensor Details" />
                  <Box sx={{ p: 3, pt: 2 }}>
                    {!selectedSensor &&
                      <Typography variant="body2" color="text.secondary">
                        Select a sensor to see its details.
                      </Typography>
                    }
                    {selectedSensor && <Stack spacing={1.5}>
                      <SensorInfoRow label="Sensor Id">{selectedSensor.sensorId}</SensorInfoRow>
                      <SensorInfoRow label="Wheel">{selectedSlotLabel || "Unassigned"}</SensorInfoRow>
                      <SensorInfoRow label="Pressure">
                        {barToPsi(selectedSensor.pressureBar).toFixed(1)} psi ({selectedSensor.pressureBar.toFixed(2)} bar)
                      </SensorInfoRow>
                      <SensorInfoRow label="Temperature">
                        {Math.round(celsiusToFahrenheit(selectedSensor.temperatureC))}°F ({selectedSensor.temperatureC}°C)
                      </SensorInfoRow>
                      <SensorInfoRow label="Battery">{selectedSensor.batteryVolts.toFixed(1)} V</SensorInfoRow>
                      <SensorInfoRow label="Reported Position">
                        {formatReportedPosition(selectedSensor.reportedPosition)}
                      </SensorInfoRow>
                      <SensorInfoRow label="Last Report">
                        {fDateTime(selectedSensor.lastReportedAt)}
                      </SensorInfoRow>
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="body2" color="text.secondary">Status</Typography>
                        <SensorStatusChips sensor={selectedSensor} />
                      </Stack>

                      <Divider />

                      <Stack direction="row" gap={1} justifyContent="flex-end">
                        {selectedSensor.wheelSlot !== null &&
                          <Button variant="outlined" onClick={unassignSelected}>
                            Unassign
                          </Button>
                        }
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
                          onClick={() => setIsOpenRemoveSensor(true)}
                        >
                          Remove Sensor
                        </Button>
                      </Stack>
                    </Stack>}
                  </Box>
                </Card>
              </Stack>
            </Grid>
          </Grid>

          <SimpleDialog
            title="Rename Device"
            open={isOpenRename}
            onCancel={() => setIsOpenRename(false)}
            onSubmit={renameDevice}
            submitDisabled={renaming}
            submitButton="Rename Device"
            submitButtonIcon={<Iconify icon="basil:save-solid" />}
            submitButtonColor="info"
          >
            <Stack direction="column" gap={1}>
              <TruxFormField label="Device Name">
                <TruxSimpleTextField
                  value={newDeviceName}
                  onChange={(val: string) => setNewDeviceName(val)}
                />
              </TruxFormField>
            </Stack>
          </SimpleDialog>

          {selectedSensor &&
            <SimpleDialog
              title="Remove Sensor"
              open={isOpenRemoveSensor}
              onCancel={() => setIsOpenRemoveSensor(false)}
              onSubmit={removeSelected}
              submitButton="Remove Sensor"
              submitButtonIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
              submitButtonColor="error"
            >
              Are you sure you would like to remove the sensor {selectedSensor.sensorId} from this device?
              It will be added back automatically if it keeps reporting.
            </SimpleDialog>
          }
        </>}
      </DisplayPageData>
    </Container>
  );
}
